/**
 * UID-68 — Contract Freeze v0 shared stub/fixtures dataset.
 *
 * The single coherent "Apex Automation — Linear Pick-and-Place Gantry Rev B" demo
 * dataset (Jira UID-39 / UID-49 / UID-57 / UID-66, provisionally accepted by the PM
 * on 2026-08-10). This is what every vertical (WS-2 Catalog, WS-3 Intake, WS-4 RFQ,
 * WS-5 Cart/Order) builds against instead of another team's runtime, so the seams
 * are exercised the same way everywhere.
 *
 * Typed against the FROZEN contract — the generated `schema.gen.ts` (api-specs @ 0.2.0,
 * composed via ADR-0025). If the contract drifts, this file stops compiling: that is
 * the point. Lives in `apps/web` because that is the only place the generated types
 * exist and where all four SPA verticals can import it (see UID-68 report for the
 * placement rationale vs. packages/shared / apps/api).
 *
 * The money ties out end-to-end (ADR-0029 rule `Σ(line.price×qty) + Σ(charge.amount)`):
 *   catalog 656.60 + custom parts 1,045.00 + charges/NRE 2,635.00 = 4,336.60 USD.
 *
 * Data-only. Convenience math (totals) belongs in the consumer / the spec test, not here.
 */
import type { components } from '../api/schema.gen'

type Schemas = components['schemas']
type UoM = Schemas['UoM']
type SourceableUom = Schemas['SourceableUom']
type Price = Schemas['Price']
type PriceProvenance = Schemas['PriceProvenance']
type DeliveryPromise = Schemas['DeliveryPromise']
type SourceableKind = Schemas['SourceableKind']
type Sourceable = Schemas['Sourceable']
type SourceableRef = Schemas['SourceableRef']
type LineItem = Schemas['LineItem']
type Rfq = Schemas['Rfq']
type Quote = Schemas['Quote']
type Cart = Schemas['Cart']
type Order = Schemas['Order']
type OrderLine = Schemas['OrderLine']
type SharedSession = Schemas['SharedSession']
type ChargeLine = Schemas['ChargeLine']
type CatalogResultCard = Schemas['CatalogResultCard']
type CatalogItemAvailability = Schemas['CatalogItemAvailability']

// ── Stable demo identities (UID-39 spine) ──────────────────────────────────────
export const ACCOUNT_ID = '7a1e0000-0000-4000-8000-000000000001'
export const ENGINEER_USER_ID = 'oidc|imts-demo-engineer-josh'
export const PURCHASER_USER_ID = 'oidc|imts-demo-purchaser-dana'
const RFQ_ID = '3f4a0000-0000-4000-8000-000000000f01'
const QUOTE_ID = '3f4a0000-0000-4000-8000-000000000f02'
const CART_ID = '3f4a0000-0000-4000-8000-000000000a01'
const ORDER_ID = '3f4a0000-0000-4000-8000-0000000000d1'
const SESSION_ID = '3f4a0000-0000-4000-8000-000000000e01'
const BRACKET_ID = '5b2c0000-0000-4000-8000-0000000000c1'
const HOUSING_ID = '5b2c0000-0000-4000-8000-0000000000c2'

// ── Value-object builders (keep the dataset DRY and tie-out-correct) ────────────
const EA: UoM = { code: 'EA', kind: 'each' }
const PACK: UoM = { code: 'PACK', kind: 'pack' }
const eaUom: SourceableUom = { saleUom: EA, pricingUom: EA }
const packUom: SourceableUom = { saleUom: PACK, pricingUom: EA, packSize: 50 }

const price = (amount: string, provenance: PriceProvenance, basis = 'EA'): Price => ({
  amount,
  currency: 'USD',
  basis,
  provenance,
})

const shipBy = (date: string, value: number, uom: UoM = EA): DeliveryPromise => ({
  policy: 'ship_by',
  schedule: [{ quantity: { value, uom }, date }],
  committedDates: [date],
  actualDates: [],
})

const pad12 = (n: number) => n.toString(16).padStart(12, '0')

// ── Demo Sourceable thumbnails (UID-401 correction 1) ───────────────────────────
// A small, non-negative, DETERMINISTIC string hash used ONLY to pick a stable
// demo image from `DEMO_CATALOG_IMAGE_REFS` below. Duplicated (not imported)
// from `apps/api/src/modules/rfq/domain.ts`'s `stableDemoHash` (UID-267) —
// `apps/web` must not import `apps/api` internals, and this is a tiny, pure,
// no-external-behavior function with nothing to drift. Not security-sensitive.
function stableDemoHash(input: string): number {
  let hash = 0
  for (let i = 0; i < input.length; i += 1) {
    hash = (hash * 31 + input.charCodeAt(i)) >>> 0
  }
  return hash
}

/**
 * The pool of DEMO part-thumbnail refs the Add-parts picker's row thumbnail
 * (`AddPartsModal.tsx` / `AddPartsToRfqModal.tsx`, UID-401 item 2) renders via
 * `Sourceable.imageRef`. Same pool, and same origin, as `apps/api/src/modules/
 * rfq/domain.ts`'s `DEMO_PART_THUMBNAIL_REFS` (UID-328 / ADR-0112) — duplicated
 * here (not imported) because this dataset's candidates are consumed directly
 * by `apps/web` verticals, never through the BFF. Per ADR-0090 D2 a ref must be
 * root-relative `/assets/<…>` AND the bytes must exist: every path below is
 * committed under `apps/web/public/assets/catalog/` (verified by
 * `apex-automation.test.ts`'s on-disk existence check, so a stale ref fails CI
 * rather than shipping a 404'd image).
 */
const DEMO_CATALOG_IMAGE_REFS: readonly [string, ...string[]] = [
  '/assets/catalog/ball-bearing.jpg',
  '/assets/catalog/linear-guide.jpg',
  '/assets/catalog/pillow-block.jpg',
  '/assets/catalog/precision-shaft.jpg',
  '/assets/catalog/truss-screw.jpg',
]

/** Deterministic imageRef pick, keyed by Sourceable id — a given part keeps
 * the same thumbnail across reads (mirrors the BFF's `stableDemoHash` posture,
 * no randomness). */
const imageRefFor = (id: string): string =>
  DEMO_CATALOG_IMAGE_REFS[stableDemoHash(id) % DEMO_CATALOG_IMAGE_REFS.length] ??
  DEMO_CATALOG_IMAGE_REFS[0]

// ── 1. Catalog Sourceables (the 10 off-the-shelf parts that thread into the order) ─
interface CatalogRow {
  n: number
  sku: string
  name: string
  kind: Extract<SourceableKind, 'catalog' | 'configurable'>
  unit: string
  qty: number
  pack?: true
}

const CATALOG: readonly CatalogRow[] = [
  {
    n: 1,
    sku: 'SSR15-400',
    name: 'Linear guide rail, 15mm, L=400',
    kind: 'configurable',
    unit: '86.00',
    qty: 2,
  },
  {
    n: 2,
    sku: 'BSSZ1605-400',
    name: 'Ball screw, Ø16 lead 5, L=400',
    kind: 'configurable',
    unit: '128.00',
    qty: 1,
  },
  {
    n: 3,
    sku: 'B6800ZZ',
    name: 'Deep-groove ball bearing 6800ZZ',
    kind: 'catalog',
    unit: '6.20',
    qty: 8,
  },
  {
    n: 4,
    sku: 'UCP204',
    name: 'Pillow-block bearing unit UCP204',
    kind: 'catalog',
    unit: '24.50',
    qty: 2,
  },
  {
    n: 5,
    sku: 'CPDW34-8-10',
    name: 'Flexible disc coupling, bore 8×10',
    kind: 'configurable',
    unit: '38.00',
    qty: 1,
  },
  {
    n: 6,
    sku: 'PSFJ12-400',
    name: 'Precision linear shaft, Ø12, L=400',
    kind: 'configurable',
    unit: '31.00',
    qty: 2,
  },
  {
    n: 7,
    sku: 'CF10-1',
    name: 'Cam follower, stud type, Ø10',
    kind: 'catalog',
    unit: '12.75',
    qty: 4,
  },
  {
    n: 8,
    sku: 'SSET-M5-8',
    name: 'Set screw M5×8, pack/50',
    kind: 'catalog',
    unit: '9.50',
    qty: 2,
    pack: true,
  },
  {
    n: 9,
    sku: 'SCWA12',
    name: 'Shaft collar / locking assembly, Ø12',
    kind: 'catalog',
    unit: '7.80',
    qty: 4,
  },
  {
    n: 10,
    sku: 'VGB-W2',
    name: 'V-groove guide bearing, W2',
    kind: 'catalog',
    unit: '14.20',
    qty: 4,
  },
]

const catalogSourceableId = (n: number) => `5a1c0000-0000-4000-8000-${pad12(n)}`

// Only `kind: 'catalog'` may omit `accountId` (packages/shared `sourceable.ts`'s
// `makeSourceableRef` invariant, ADR-0002/0004): catalog Sourceables are global,
// but `configurable`/`custom`/`assembly_bom` are tenant-owned and REQUIRE one. Two
// of CATALOG's rows are `kind: 'configurable'` (SSR15-400, BSSZ1605-400 — plus
// CPDW34-8-10 and PSFJ12-400), so every mapper below must set `accountId` for
// every non-catalog row or a downstream write (e.g. UID-56's RFQ build screen
// picking one of these, then an accept-to-cart) 422s on the invariant. Bug
// found by QA's UID-56 walkthrough; guarded by apex-automation.test.ts's
// "every non-catalog Sourceable carries an accountId" spec so it can't
// regress silently.
const tenantOwner = (kind: CatalogRow['kind']) =>
  kind === 'catalog' ? {} : { accountId: ACCOUNT_ID }

const catalogSourceables: readonly Sourceable[] = CATALOG.map((r) => ({
  id: catalogSourceableId(r.n),
  kind: r.kind,
  ...tenantOwner(r.kind),
  uom: r.pack ? packUom : eaUom,
  name: r.name,
  customerBomId: null,
  catalogSku: r.sku,
  indicativePrice: price(r.unit, 'catalog', r.pack ? 'PACK' : 'EA'),
  // UID-68 proposed projection fields (populated so the stub is complete):
  provenance: 'misumi_standard',
  libraryStatus: 'catalog',
  pricingProjection: { mode: 'auto_priced' },
  // UID-401 correction 1 — see DEMO_CATALOG_IMAGE_REFS above.
  imageRef: imageRefFor(catalogSourceableId(r.n)),
}))

const catalogRef = (r: CatalogRow): SourceableRef => ({
  id: catalogSourceableId(r.n),
  kind: r.kind,
  ...tenantOwner(r.kind),
  uom: r.pack ? packUom : eaUom,
})

// Extra catalog SKUs, addable via the SKU/CSV add flows to demo longer carts +
// pagination. Deliberately NOT part of the canonical apex-automation cart/order, so
// the $4,336.60 tie-out and the 10/12 line counts are untouched. n continues from
// CATALOG (11+) so the Sourceable ids stay unique. TEMPORARY demo data.
const EXTRA_CATALOG: readonly CatalogRow[] = [
  {
    n: 11,
    sku: 'LHFS8',
    name: 'Flanged linear bushing, Ø8',
    kind: 'catalog',
    unit: '18.40',
    qty: 1,
  },
  { n: 12, sku: 'MSFSS6', name: 'Stainless dowel pin, Ø6', kind: 'catalog', unit: '3.10', qty: 1 },
  {
    n: 13,
    sku: 'HRAT10',
    name: 'Rack gear, module 1, L=100',
    kind: 'catalog',
    unit: '21.75',
    qty: 1,
  },
  { n: 14, sku: 'SPWF20', name: 'Spring washer, Ø20', kind: 'catalog', unit: '1.95', qty: 1 },
  {
    n: 15,
    sku: 'BGRB6902',
    name: 'Deep-groove bearing 6902',
    kind: 'catalog',
    unit: '8.60',
    qty: 1,
  },
  { n: 16, sku: 'TBRN12', name: 'Timing belt pulley, 12T', kind: 'catalog', unit: '16.30', qty: 1 },
  { n: 17, sku: 'CLGP15', name: 'Clamp lever, M6', kind: 'catalog', unit: '5.45', qty: 1 },
  { n: 18, sku: 'OLDR22', name: 'Oil-less bushing, Ø22', kind: 'catalog', unit: '11.05', qty: 1 },
]

const extraCatalogSourceables: readonly Sourceable[] = EXTRA_CATALOG.map((r) => ({
  id: catalogSourceableId(r.n),
  kind: r.kind,
  ...tenantOwner(r.kind),
  uom: r.pack ? packUom : eaUom,
  name: r.name,
  customerBomId: null,
  catalogSku: r.sku,
  indicativePrice: price(r.unit, 'catalog', r.pack ? 'PACK' : 'EA'),
  provenance: 'misumi_standard',
  libraryStatus: 'catalog',
  pricingProjection: { mode: 'auto_priced' },
  // UID-401 correction 1 — see DEMO_CATALOG_IMAGE_REFS above.
  imageRef: imageRefFor(catalogSourceableId(r.n)),
}))

// ── 1b. "Recently viewed" curated catalog rows (ADR-0128, RETIRED as the row's
// data source) — kept as fixture data only ──────────────────────────────────
// ADR-0128's static, curated "Recently viewed" row has been REPLACED by real,
// per-user, 100%-client-side view-history tracking (`recently-viewed-store.ts`
// / `useRecentlyViewed.ts`) — `CatalogBrowsePage.tsx`'s `<RecentlyViewed>` no
// longer reads `recentlyViewedCatalogCards` at all. This array and its two
// exports (`recentlyViewedCatalogSourceables`, `recentlyViewedCatalogCards`)
// are kept ONLY because `cart-catalog-lookup.ts`'s `CATALOG_BY_SKU` still
// unions these SKUs in for add-row/CSV-import lookup coherence, and
// `apex-automation.test.ts` pins their shape. A SEPARATE array, `n` continuing
// from 19 (`CATALOG` is 1-10, `EXTRA_CATALOG` is 11-18): must NOT be appended
// to either of those two arrays, because `apex-automation.test.ts` pins the
// $4,336.60 tie-out and the 10/12 line counts against `CATALOG`, and
// `EXTRA_CATALOG` is the distinct SKU/CSV-add pool (ADR-0128 Decision 2).
// Every row is `kind: 'catalog'` (never `configurable` — ADR-0097: a
// configurable card renders "Configure" and needs a real backend resolve
// this mock data can't satisfy).
//
// Field values are PORTED VERBATIM from the design prototype's `parts()`
// table (`Fictiv Platform.dc.html:6555-6572`) for the eight ids in its
// hardcoded `featPartIds` (~L8444), in that exact order (the order carries
// no semantics to preserve): p5, p2, p16, p4, p15, p24, p25, p22. `cat`
// verbatim too (`Fasteners`, not "Fasteners & Hardware") — ADR-0128
// Decision 3's mapping table sends it straight to `CatalogResultCard.category`.
interface RecentlyViewedRow {
  readonly n: number
  readonly sku: string
  readonly name: string
  readonly category: string
  /** USD unit price, ported verbatim from the prototype's `price` (a
   * number there; a decimal string here, per `Price.amount`). */
  readonly unit: string
  readonly inStock: boolean
  /** Ported verbatim from the prototype's `lead` string (e.g. `'2-3 days'`),
   * parsed into `{min, max}` below rather than re-authored. */
  readonly lead: `${number}-${number} days`
  /** Rebased onto this repo's asset root per ADR-0128 Decision 4
   * (`assets/parts/x.jpg` → `/assets/catalog/x.jpg`); all eight files are
   * already committed under `apps/web/public/assets/catalog/` — zero new
   * assets needed. Deliberately NOT `imageRefFor()` (a hash pick from a
   * 5-image pool) — the prototype names one specific photo per part. */
  readonly imageRef: string
}

const RECENTLY_VIEWED_ROWS: readonly RecentlyViewedRow[] = [
  {
    n: 19,
    sku: 'PSFJ12-200',
    name: 'Precision Ground Shaft',
    category: 'Shafts & Couplings',
    unit: '28.60',
    inStock: true,
    lead: '2-3 days',
    imageRef: '/assets/catalog/precision-shaft.jpg',
  },
  {
    n: 20,
    sku: '6205-2RS',
    name: 'Deep Groove Ball Bearing 6205',
    category: 'Bearings',
    unit: '12.30',
    inStock: true,
    lead: '1-2 days',
    imageRef: '/assets/catalog/ball-bearing.jpg',
  },
  {
    n: 21,
    sku: 'MCJA-25-8-10',
    name: 'Flexible Jaw Coupling',
    category: 'Shafts & Couplings',
    unit: '18.90',
    inStock: true,
    lead: '2-3 days',
    imageRef: '/assets/catalog/disc-coupling.jpg',
  },
  {
    n: 22,
    sku: 'SHCS-M8-30-SS',
    name: 'Socket Head Cap Screw M8×30',
    category: 'Fasteners',
    unit: '0.45',
    inStock: true,
    lead: '1-2 days',
    imageRef: '/assets/catalog/cap-cover.jpg',
  },
  {
    n: 23,
    sku: 'BNK1204-3GT+310LC5',
    name: 'Ball Screw Assembly BNK1204',
    category: 'Linear Motion',
    unit: '142.80',
    inStock: false,
    lead: '5-8 days',
    imageRef: '/assets/catalog/ballscrew.jpg',
  },
  {
    n: 24,
    sku: 'NVRBK-16-M6',
    name: 'V-Groove Track Roller',
    category: 'Bearings',
    unit: '14.80',
    inStock: true,
    lead: '2-3 days',
    imageRef: '/assets/catalog/vgroove.jpg',
  },
  {
    n: 25,
    sku: 'UCP205-16',
    name: 'Pillow Block Bearing UCP205',
    category: 'Bearings',
    unit: '22.60',
    inStock: true,
    lead: '2-3 days',
    imageRef: '/assets/catalog/pillow-block.jpg',
  },
  {
    n: 26,
    sku: 'MCCLA-20-6-8',
    name: 'Aluminum Slit Disc Coupling',
    category: 'Shafts & Couplings',
    unit: '32.40',
    inStock: true,
    lead: '2-3 days',
    imageRef: '/assets/catalog/disc-coupling.jpg',
  },
]

/** `'2-3 days'` → `{min: 2, max: 3}` — the one parse this module needs of the
 * prototype's verbatim `lead` string, so `RECENTLY_VIEWED_ROWS` can carry
 * the source string unmodified rather than a pre-split pair a future edit
 * could let drift out of sync with it. */
function parseLeadDays(lead: string): { min: number; max: number } {
  const [min, max] = lead.split('-').map((part) => Number.parseInt(part, 10))
  return { min: min ?? 0, max: max ?? min ?? 0 }
}

/** `CatalogItemAvailability` for one curated row (ADR-0128 Decision 3):
 * `stock:true`/`false` → `status: 'in_stock'`/`'made_to_order'`,
 * `leadTimeDays` from `parseLeadDays`, `note` in the same
 * "<Status> · <min>–<max> days" form `ResultCard.tsx`'s other availability
 * notes use, and `availableQuantity: null` always (real inventory is
 * UID-38 — never fabricate a quantity). */
function recentlyViewedAvailability(row: RecentlyViewedRow): CatalogItemAvailability {
  const { min, max } = parseLeadDays(row.lead)
  const status: CatalogItemAvailability['status'] = row.inStock ? 'in_stock' : 'made_to_order'
  const statusLabel = row.inStock ? 'In stock' : 'Made to order'
  return {
    availableQuantity: null,
    status,
    note: `${statusLabel} · ${min}–${max} days`,
    leadTimeDays: { min, max },
  }
}

const recentlyViewedCatalogSourceables: readonly Sourceable[] = RECENTLY_VIEWED_ROWS.map((r) => ({
  id: catalogSourceableId(r.n),
  kind: 'catalog',
  uom: eaUom,
  name: r.name,
  customerBomId: null,
  catalogSku: r.sku,
  indicativePrice: price(r.unit, 'catalog', 'EA'),
  provenance: 'misumi_standard',
  libraryStatus: 'catalog',
  pricingProjection: { mode: 'auto_priced' },
  imageRef: r.imageRef,
}))

/**
 * The full `CatalogResultCard` envelope (sourceable + availability +
 * category) for the RETIRED curated "Recently viewed" row — see the module
 * comment above `RECENTLY_VIEWED_ROWS`: `RecentlyViewed.tsx` no longer reads
 * this; it's kept only for `cart-catalog-lookup.ts`'s SKU union and this
 * file's own tie-out test. Order matches `RECENTLY_VIEWED_ROWS` (the
 * prototype's `featPartIds` order); no sort, no truncation.
 */
const recentlyViewedCatalogCards: readonly CatalogResultCard[] = RECENTLY_VIEWED_ROWS.map(
  (row, index) => ({
    sourceable: recentlyViewedCatalogSourceables[index] as Sourceable,
    availability: recentlyViewedAvailability(row),
    category: row.category,
  }),
)

// ── 2. Custom Sourceables (the two CAD parts → both quote paths) ────────────────
const customSourceables: readonly Sourceable[] = [
  {
    id: BRACKET_ID,
    kind: 'custom',
    accountId: ACCOUNT_ID,
    uom: eaUom,
    name: 'Motor mount bracket',
    customerBomId: 'BOM-ALPHA',
    catalogSku: null,
    configAttributes: {
      partCorrelationId: 'bom-alpha-cnc-bracket',
      process: 'cnc',
      material: 'Aluminum 6061-T6',
      finish: 'anodizing-2/black-sealed',
      cert: 'material-certification',
      rev: 'A',
      provenance: 'fictiv_custom',
    },
    indicativePrice: price('42.50', 'parametric'),
    // UID-68 proposed projection fields — auto-quote path, locked to the accepted quote:
    provenance: 'fictiv_custom',
    libraryStatus: 'quoted',
    dfmSummary: {
      manufacturable: true,
      findings: [],
      suggestedProcess: 'CNC Milling',
      suggestedMaterial: 'Aluminum 6061-T6',
      indicativeLeadTimeDays: 10,
    },
    pricingProjection: { mode: 'locked_to_rfq', lockedRfqId: RFQ_ID, lockedQuoteId: QUOTE_ID },
    // UID-401 correction 1 — see DEMO_CATALOG_IMAGE_REFS above.
    imageRef: imageRefFor(BRACKET_ID),
  },
  {
    id: HOUSING_ID,
    kind: 'custom',
    accountId: ACCOUNT_ID,
    uom: eaUom,
    name: 'Sensor enclosure housing',
    customerBomId: 'BOM-ALPHA',
    catalogSku: null,
    configAttributes: {
      partCorrelationId: 'bom-alpha-im-housing',
      process: 'im',
      material: 'ABS',
      finish: 'none',
      cert: 'certificate-of-compliance',
      rev: 'C',
      provenance: 'fictiv_custom',
    },
    // no indicativePrice — DFM fail, no auto-price (UID-49)
    // UID-68 proposed projection fields — DFM fail → CPQ manual takeover path:
    provenance: 'fictiv_custom',
    libraryStatus: 'quoted',
    dfmSummary: {
      manufacturable: false,
      findings: [
        'Wall thickness 0.4mm is below the 1.0mm minimum for injection molding',
        'No draft angle on the side walls — add >= 1 degree for ejection',
      ],
      suggestedProcess: 'Injection Molding',
      suggestedMaterial: 'ABS',
      indicativeLeadTimeDays: null,
    },
    pricingProjection: { mode: 'quote_required' },
    // UID-401 correction 1 — see DEMO_CATALOG_IMAGE_REFS above.
    imageRef: imageRefFor(HOUSING_ID),
  },
]

const bracketRef: SourceableRef = {
  id: BRACKET_ID,
  kind: 'custom',
  accountId: ACCOUNT_ID,
  uom: eaUom,
}
const housingRef: SourceableRef = {
  id: HOUSING_ID,
  kind: 'custom',
  accountId: ACCOUNT_ID,
  uom: eaUom,
}

// ── 3. ChargeLines (ADR-0029 add-ons / NRE — first-class, NOT synthetic SVC-* parts) ─
const charges: readonly ChargeLine[] = [
  {
    id: '9c0e0000-0000-4000-8000-000000000001',
    kind: 'tooling',
    description: 'Soft tooling NRE',
    amount: price('2400.00', 'quoted', 'lot'),
    appliesToSourceableId: HOUSING_ID,
  },
  {
    id: '9c0e0000-0000-4000-8000-000000000002',
    kind: 'certification',
    description: 'Material Certification (Cert of Conformity)',
    amount: price('85.00', 'quoted', 'lot'),
    appliesToSourceableId: HOUSING_ID,
  },
  {
    id: '9c0e0000-0000-4000-8000-000000000003',
    kind: 'inspection',
    description: 'Advanced Inspection Report',
    amount: price('150.00', 'quoted', 'lot'),
    appliesToSourceableId: BRACKET_ID,
  },
]

// ── 4. Rfq (both custom lines; quoted end-state) ────────────────────────────────
const rfq: Rfq = {
  id: RFQ_ID,
  accountId: ACCOUNT_ID,
  status: 'quoted',
  quoteId: QUOTE_ID,
  poReference: null,
  createdAt: '2026-09-16T15:10:00Z',
  expiresAt: '2026-10-16T23:59:59Z',
  // quotingSubState absent — cleared once status left `quoting` (ADR-0029)
  lines: [
    { status: 'pending', sourceable: bracketRef, quantity: { value: 4, uom: EA } },
    { status: 'pending', sourceable: housingRef, quantity: { value: 100, uom: EA } },
  ],
}

// ── 5. Quote — accepted end-state, carrying the ADR-0029 charges[] ──────────────
const bracketQuoteLine: LineItem = {
  status: 'ready',
  sourceable: bracketRef,
  quantity: { value: 4, uom: EA },
  price: price('42.50', 'quoted'),
  deliveryPromise: shipBy('2026-09-30', 4),
  sourceRfqId: RFQ_ID,
}
const housingQuoteLine: LineItem = {
  status: 'ready',
  sourceable: housingRef,
  quantity: { value: 100, uom: EA },
  price: price('8.75', 'quoted'),
  deliveryPromise: shipBy('2026-10-14', 100),
  sourceRfqId: RFQ_ID,
}

const quote: Quote = {
  id: QUOTE_ID,
  rfqId: RFQ_ID,
  accountId: ACCOUNT_ID,
  state: 'accepted',
  validUntil: '2026-10-16T23:59:59Z',
  createdAt: '2026-09-16T15:40:00Z',
  lines: [bracketQuoteLine, housingQuoteLine],
  charges: [...charges],
}

/**
 * The same Quote earlier in its lifecycle — the CPQ-takeover moment (UID-57 state
 * table: housing Requested → AutoQuoting → ManualTakeover → Quoted). Gives WS-4 a
 * fixture to render the ADR-0029 `quotingSubState` / `takeoverReason` from; the
 * canonical `quote` above is the accepted end-state where the sub-state is cleared.
 */
const quoteWhileQuoting: Quote = {
  id: QUOTE_ID,
  rfqId: RFQ_ID,
  accountId: ACCOUNT_ID,
  state: 'quoting',
  quotingSubState: 'manual_takeover',
  takeoverReason: 'DFM fail (wall thickness / draft)',
  validUntil: null,
  createdAt: '2026-09-16T15:40:00Z',
  lines: [
    {
      status: 'ready',
      sourceable: bracketRef,
      quantity: { value: 4, uom: EA },
      price: price('42.50', 'quoted'),
    },
    { status: 'pending', sourceable: housingRef, quantity: { value: 100, uom: EA } },
  ],
}

// ── 6. Cart — mixed-readiness, all lines ready at checkout (UID-66) ─────────────
const catalogCartLines: readonly LineItem[] = CATALOG.map((r) => ({
  status: 'ready',
  sourceable: catalogRef(r),
  quantity: { value: r.qty, uom: r.pack ? PACK : EA },
  price: price(r.unit, 'catalog', r.pack ? 'PACK' : 'EA'),
  deliveryPromise: shipBy('2026-09-22', r.qty, r.pack ? PACK : EA),
}))

const customCartLines: readonly LineItem[] = [
  {
    status: 'ready',
    sourceable: bracketRef,
    quantity: { value: 4, uom: EA },
    price: price('42.50', 'quoted'),
    deliveryPromise: shipBy('2026-09-30', 4),
    sourceRfqId: RFQ_ID,
    sourceQuoteId: QUOTE_ID,
  },
  {
    status: 'ready',
    sourceable: housingRef,
    quantity: { value: 100, uom: EA },
    price: price('8.75', 'quoted'),
    deliveryPromise: shipBy('2026-10-14', 100),
    sourceRfqId: RFQ_ID,
    sourceQuoteId: QUOTE_ID,
  },
]

const cart: Cart = {
  id: CART_ID,
  accountId: ACCOUNT_ID,
  lines: [...catalogCartLines, ...customCartLines],
  poReference: 'PO-APX-2026-0917',
  charges: [...charges],
  createdAt: '2026-09-16T16:10:00Z',
  updatedAt: '2026-09-16T16:15:00Z',
}

// ── 7. Order — 12 part lines + 3 charges (ADR-0029: NOT 15 synthetic lines) ─────
const catalogOrderLines: readonly OrderLine[] = CATALOG.map((r) => ({
  id: `6c0d0000-0000-4000-8000-${pad12(r.n)}`,
  sourceable: catalogRef(r),
  quantity: { value: r.qty, uom: r.pack ? PACK : EA },
  price: price(r.unit, 'catalog', r.pack ? 'PACK' : 'EA'),
  deliveryPromise: shipBy('2026-09-22', r.qty, r.pack ? PACK : EA),
  status: 'placed',
}))

const customOrderLines: readonly OrderLine[] = [
  {
    id: `6c0d0000-0000-4000-8000-${pad12(11)}`,
    sourceable: bracketRef,
    quantity: { value: 4, uom: EA },
    price: price('42.50', 'quoted'),
    deliveryPromise: shipBy('2026-09-30', 4),
    status: 'placed',
  },
  {
    id: `6c0d0000-0000-4000-8000-${pad12(12)}`,
    sourceable: housingRef,
    quantity: { value: 100, uom: EA },
    price: price('8.75', 'quoted'),
    deliveryPromise: shipBy('2026-10-14', 100),
    status: 'placed',
  },
]

const order: Order = {
  id: ORDER_ID,
  accountId: ACCOUNT_ID,
  status: 'placed',
  lines: [...catalogOrderLines, ...customOrderLines],
  poReference: 'PO-APX-2026-0917',
  netsuiteSalesOrderId: 'SO-104471',
  salesforceOpportunityId: '0065f00000ABC123',
  placedAt: '2026-09-16T16:20:00Z',
  createdAt: '2026-09-16T16:18:00Z',
  charges: [...charges],
}

// ── 8. SharedSession — Act-3 ready and Act-4 opened states (UID-57 / UID-66) ────
const sharedSessionSummary: NonNullable<SharedSession['summary']> = {
  lineCount: 12,
  chargeCount: 3,
  currency: 'USD',
  subtotalAmount: '4336.60',
}

const sharedSessionReady: SharedSession = {
  id: SESSION_ID,
  accountId: ACCOUNT_ID,
  status: 'ready',
  createdByUserId: ENGINEER_USER_ID,
  shareToken: 'ss_demo_apex_gantry',
  shareUrl: 'https://demo.misumi-unified.app/s/demo-apex-gantry',
  rfqId: RFQ_ID,
  cartId: null,
  openedByUserId: null,
  expiresAt: '2026-09-23T23:59:59Z',
  createdAt: '2026-09-16T15:45:00Z',
  openedAt: null,
  // UID-68 proposed payload-sufficiency fields:
  quoteId: QUOTE_ID,
  orderId: null,
  title: 'Apex Automation — Gantry Rev B',
  summary: sharedSessionSummary,
}

const sharedSession: SharedSession = {
  ...sharedSessionReady,
  status: 'opened',
  cartId: CART_ID,
  openedByUserId: PURCHASER_USER_ID,
  openedAt: '2026-09-16T16:05:00Z',
  orderId: ORDER_ID,
}

/**
 * The whole coherent dataset, one export. Import a slice or the whole thing:
 *   import { apexAutomationGantry } from '@/lib/fixtures/apex-automation'
 */
export const apexAutomationGantry = {
  accountId: ACCOUNT_ID,
  engineerUserId: ENGINEER_USER_ID,
  purchaserUserId: PURCHASER_USER_ID,
  catalogSourceables,
  extraCatalogSourceables,
  recentlyViewedCatalogSourceables,
  recentlyViewedCatalogCards,
  customSourceables,
  charges,
  rfq,
  quote,
  quoteWhileQuoting,
  cart,
  order,
  sharedSession,
  sharedSessionReady,
} as const
